import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import DashboardLayout from '../../components/layout/DashboardLayout';
import { dashboardAPI, employeeAPI } from '../../api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '../../components/ui/avatar';
import { Progress } from '../../components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { toast } from 'sonner';
import { getInitials, formatTime, getStatusColor } from '../../lib/utils';
import {
  ArrowLeft,
  Users,
  UserCheck,
  UserX,
  Calendar,
  Clock,
  AlertTriangle,
  CheckCircle,
  XCircle
} from 'lucide-react';

const CompanyDeepDive = () => {
  const { companyId } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    if (!companyId) return;
    try {
      const response = await dashboardAPI.getCompanyDashboard(companyId);
      setData(response.data);
    } catch (err) {
      toast.error('Failed to load company data');
    } finally {
      setLoading(false);
    }
  }, [companyId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  if (loading) {
    return (
      <DashboardLayout title="Loading...">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
      </DashboardLayout>
    );
  }

  const { company, stats } = data || {};

  return (
    <DashboardLayout 
      title={company?.name || 'Company'} 
      subtitle="Deep dive into company performance"
    >
      {/* Back Button */}
      <div className="mb-6">
        <Link to="/app/owner">
          <Button variant="ghost" className="gap-2" data-testid="back-to-portfolio">
            <ArrowLeft className="h-4 w-4" />
            Back to Portfolio
          </Button>
        </Link>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4 mb-8">
        <Card className="stat-card">
          <CardContent className="pt-4">
            <div className="flex items-center gap-2">
              <Users className="h-5 w-5 text-primary" />
              <div>
                <p className="text-2xl font-bold">{stats?.total_employees || 0}</p>
                <p className="text-xs text-muted-foreground">Total Staff</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="stat-card">
          <CardContent className="pt-4">
            <div className="flex items-center gap-2">
              <UserCheck className="h-5 w-5 text-emerald-500" />
              <div>
                <p className="text-2xl font-bold">{stats?.present_today || 0}</p>
                <p className="text-xs text-muted-foreground">Present</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="stat-card">
          <CardContent className="pt-4">
            <div className="flex items-center gap-2">
              <UserX className="h-5 w-5 text-red-500" />
              <div>
                <p className="text-2xl font-bold">{stats?.absent_today || 0}</p>
                <p className="text-xs text-muted-foreground">Absent</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="stat-card">
          <CardContent className="pt-4">
            <div className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-amber-500" />
              <div>
                <p className="text-2xl font-bold">{stats?.late_today || 0}</p>
                <p className="text-xs text-muted-foreground">Late</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="stat-card">
          <CardContent className="pt-4">
            <div className="flex items-center gap-2">
              <Calendar className="h-5 w-5 text-blue-500" />
              <div>
                <p className="text-2xl font-bold">{stats?.on_leave_today || 0}</p>
                <p className="text-xs text-muted-foreground">On Leave</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="stat-card">
          <CardContent className="pt-4">
            <div className="flex items-center gap-2">
              <Clock className="h-5 w-5 text-purple-500" />
              <div>
                <p className="text-2xl font-bold">{stats?.total_overtime_hours || 0}h</p>
                <p className="text-xs text-muted-foreground">OT Today</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <Tabs defaultValue="workforce" className="space-y-6">
        <TabsList>
          <TabsTrigger value="workforce" data-testid="tab-workforce">Live Workforce</TabsTrigger>
          <TabsTrigger value="approvals" data-testid="tab-approvals">
            Pending Approvals
            {(stats?.pending_leaves > 0 || stats?.pending_shift_changes > 0) && (
              <Badge variant="destructive" className="ml-2">
                {stats?.pending_leaves + stats?.pending_shift_changes}
              </Badge>
            )}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="workforce" className="space-y-6">
          {/* Present Employees */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <UserCheck className="h-5 w-5 text-emerald-500" />
                Present Today ({data?.present_employees?.length || 0})
              </CardTitle>
            </CardHeader>
            <CardContent>
              {data?.present_employees?.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {data.present_employees.map((emp) => (
                    <div key={emp.id} className="flex items-center gap-3 p-3 rounded-lg border border-border">
                      <Avatar>
                        <AvatarImage src={emp.avatar_url} />
                        <AvatarFallback>{getInitials(emp.first_name, emp.last_name)}</AvatarFallback>
                      </Avatar>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium truncate">{emp.first_name} {emp.last_name}</p>
                        <p className="text-xs text-muted-foreground">
                          In: {formatTime(emp.attendance?.clock_in)}
                        </p>
                      </div>
                      <Badge className="status-present">Present</Badge>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-muted-foreground text-center py-8">No employees present yet</p>
              )}
            </CardContent>
          </Card>

          {/* Late Employees */}
          {data?.late_employees?.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <AlertTriangle className="h-5 w-5 text-amber-500" />
                  Late Today ({data.late_employees.length})
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {data.late_employees.map((emp) => (
                    <div key={emp.id} className="flex items-center gap-3 p-3 rounded-lg border border-amber-200 bg-amber-50">
                      <Avatar>
                        <AvatarImage src={emp.avatar_url} />
                        <AvatarFallback>{getInitials(emp.first_name, emp.last_name)}</AvatarFallback>
                      </Avatar>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium truncate">{emp.first_name} {emp.last_name}</p>
                        <p className="text-xs text-amber-700">
                          {emp.attendance?.late_minutes} mins late
                        </p>
                      </div>
                      <Badge className="status-late">Late</Badge>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* On Leave */}
          {data?.on_leave_employees?.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Calendar className="h-5 w-5 text-blue-500" />
                  On Leave Today ({data.on_leave_employees.length})
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {data.on_leave_employees.map((emp) => (
                    <div key={emp.id} className="flex items-center gap-3 p-3 rounded-lg border border-blue-200 bg-blue-50">
                      <Avatar>
                        <AvatarImage src={emp.avatar_url} />
                        <AvatarFallback>{getInitials(emp.first_name, emp.last_name)}</AvatarFallback>
                      </Avatar>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium truncate">{emp.first_name} {emp.last_name}</p>
                        <p className="text-xs text-blue-700">On leave</p>
                      </div>
                      <Badge className="status-leave">Leave</Badge>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Absent */}
          {data?.absent_employees?.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <UserX className="h-5 w-5 text-red-500" />
                  Not Yet In ({data.absent_employees.length})
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {data.absent_employees.slice(0, 9).map((emp) => (
                    <div key={emp.id} className="flex items-center gap-3 p-3 rounded-lg border border-red-200 bg-red-50">
                      <Avatar>
                        <AvatarImage src={emp.avatar_url} />
                        <AvatarFallback>{getInitials(emp.first_name, emp.last_name)}</AvatarFallback>
                      </Avatar>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium truncate">{emp.first_name} {emp.last_name}</p>
                        <p className="text-xs text-red-700 capitalize">{emp.role}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="approvals" className="space-y-6">
          {/* Pending Leave Requests */}
          <Card>
            <CardHeader>
              <CardTitle>Pending Leave Requests ({data?.pending_leaves?.length || 0})</CardTitle>
            </CardHeader>
            <CardContent>
              {data?.pending_leaves?.length > 0 ? (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Employee</TableHead>
                      <TableHead>Dates</TableHead>
                      <TableHead>Reason</TableHead>
                      <TableHead>Status</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {data.pending_leaves.map((leave) => (
                      <TableRow key={leave.id}>
                        <TableCell>{leave.employee_id}</TableCell>
                        <TableCell>{leave.start_date} - {leave.end_date}</TableCell>
                        <TableCell>{leave.reason}</TableCell>
                        <TableCell>
                          <Badge className="status-pending">Pending</Badge>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              ) : (
                <p className="text-muted-foreground text-center py-8">No pending leave requests</p>
              )}
            </CardContent>
          </Card>

          {/* Pending Shift Changes */}
          <Card>
            <CardHeader>
              <CardTitle>Pending Shift Changes ({data?.pending_shift_changes?.length || 0})</CardTitle>
            </CardHeader>
            <CardContent>
              {data?.pending_shift_changes?.length > 0 ? (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Employee</TableHead>
                      <TableHead>Date</TableHead>
                      <TableHead>Reason</TableHead>
                      <TableHead>Status</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {data.pending_shift_changes.map((change) => (
                      <TableRow key={change.id}>
                        <TableCell>{change.employee_id}</TableCell>
                        <TableCell>{change.date}</TableCell>
                        <TableCell>{change.reason}</TableCell>
                        <TableCell>
                          <Badge className="status-pending">Pending</Badge>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              ) : (
                <p className="text-muted-foreground text-center py-8">No pending shift changes</p>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </DashboardLayout>
  );
};

export default CompanyDeepDive;
